// src/components/OtpVerify.jsx
//
// Per an earlier request, restyled to match Login.jsx's auth-box/card
// split-screen shell instead of the old Login.css grid layout. Now
// further rebuilt to match C:\CET-HTML-Code\verify-otp.html
// field-for-field: the "OTP sent on {email}" heading, the six separate
// auto-advancing digit boxes ("Enter your 6-digit code"), the "Verify
// OTP" button label, the "Don't have a code? Resend" line, and a
// "Return to Sign in" link -- previously missing here entirely. The
// working resend countdown (disabled for 5 minutes after each send) is
// kept and folded into the "Don't have a code?" line rather than
// replaced with the mockup's plain non-functional link.
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_ORIGIN, setAuthToken } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

export default function OtpVerify() {
  const email = localStorage.getItem("login_email") || "";
  const [otp, setOtp] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendTimer, setResendTimer] = useState(300); // seconds
  // IMPL-10: set once /verify-otp responds with totp_required (the
  // account has opted into authenticator-app MFA from Profile >
  // Security) -- swaps this same screen over to asking for that code
  // and posting it to /verify-totp instead of finishing on the email
  // OTP alone. Everyone who hasn't enrolled never sees this at all.
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  const otpBoxRefs = useRef([]);

  useEffect(() => {
    // start resend countdown if needed
    let timer;
    if (resendDisabled) {
      timer = setInterval(() => {
        setResendTimer((t) => {
          if (t <= 1) {
            clearInterval(timer);
            setResendDisabled(false);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendDisabled]);

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    setMsg("");
    if (!otp || otp.trim().length < 6) {
      setMsg("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_ORIGIN}/api/verify-otp`,
        { email, otp: otp.trim() },
        // IMPL-03: needed so the HttpOnly session cookie /verify-otp
        // sets on success is actually accepted by the browser
        // cross-origin (local dev: :3000 -> :5000).
        { withCredentials: true }
      );

      if (res.data && res.data.totp_required) {
        setTotpRequired(true);
        setMsg("");
        return;
      }

      if (res.data && res.data.success) {
        localStorage.setItem("user_email", email);
        // This is the piece that was missing -- Header.jsx, Layout.jsx
        // and ProfilePage.jsx all read this exact key to know who's
        // logged in. res.data.user only exists once app.py is running
        // the login_api.py I sent (the one whose /verify-otp returns
        // {success, user: {id, email, UserName, RoleID, RoleName}});
        // the `if` guard keeps this safe even if that hasn't been
        // deployed yet -- it just won't populate the header until it is.
        if (res.data.user) {
          localStorage.setItem("currentUser", JSON.stringify(res.data.user));
        }
        // NEW: every other API route now requires a JWT (see
        // @jwt_required in app.py) -- this is the token /verify-otp
        // just issued. Stored under the same key apiConfig.js's
        // authFetch()/api axios instance read from automatically, so
        // every page's API calls carry it with no per-page code needed.
        // Without this, every page past this point would 401 on its
        // very first API call.
        if (res.data.token) {
          setAuthToken(res.data.token);
        }
        window.location.href = ROUTES.DASHBOARD;
      } else {
        setMsg(res.data?.message || "Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      // account_locked (IMPL-02) comes back as a non-2xx status with a
      // real, user-facing message ("Too many failed attempts...") --
      // show that instead of a generic "Server error" when present.
      setMsg(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  // IMPL-10 second factor -- posts the authenticator code to
  // /verify-totp once /verify-otp has already confirmed the email OTP
  // and flagged this account as TOTP-enrolled (totpRequired above).
  const verifyTotp = async (e) => {
    if (e) e.preventDefault();
    setMsg("");
    if (!totpCode || totpCode.trim().length < 6) {
      setMsg("Enter the 6-digit authenticator code");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_ORIGIN}/api/verify-totp`,
        { email, code: totpCode.trim() },
        { withCredentials: true }
      );

      if (res.data && res.data.success) {
        localStorage.setItem("user_email", email);
        if (res.data.user) {
          localStorage.setItem("currentUser", JSON.stringify(res.data.user));
        }
        if (res.data.token) {
          setAuthToken(res.data.token);
        }
        window.location.href = ROUTES.DASHBOARD;
      } else {
        setMsg(res.data?.message || "Invalid authenticator code");
      }
    } catch (err) {
      console.error(err);
      setMsg(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async (e) => {
    e?.preventDefault();
    if (resendDisabled) return;
    setMsg("");
    try {
      await axios.post(`${API_ORIGIN}/api/resend-otp`, { email });
      setMsg("OTP resent");
      setResendDisabled(true);
      setResendTimer(300);
    } catch (err) {
      console.error(err);
      setMsg("Unable to resend OTP");
    }
  };

  const handleOtpBoxChange = (index, rawValue) => {
    const digits = rawValue.replace(/[^0-9]/g, "");
    // Normally rawValue is a single typed digit (each box has maxLength=1),
    // but this also runs after handleOtpBoxPaste's own preventDefault-ed
    // paste (which sets the boxes directly and never calls this), AND as
    // a fallback for anything that still lands here with more than one
    // digit -- e.g. mobile "fill from SMS" autofill that some browsers
    // deliver as a plain onChange rather than a clipboard paste event.
    // Same fill-forward-from-this-box behavior as the dedicated paste
    // handler below, just triggered a different way.
    if (digits.length > 1) {
      const boxes = otp.padEnd(6, " ").split("");
      let i = index;
      for (const d of digits) {
        if (i > 5) break;
        boxes[i] = d;
        i++;
      }
      setOtp(boxes.join("").trimEnd());
      otpBoxRefs.current[Math.min(i, 5)]?.focus();
      return;
    }
    const boxes = otp.padEnd(6, " ").split("");
    boxes[index] = digits || " ";
    setOtp(boxes.join("").trimEnd());
    if (digits && index < 5) {
      otpBoxRefs.current[index + 1]?.focus();
    }
  };

  // FIX: pasting the full 6-digit OTP into any one box used to only ever
  // fill that single box (with just the LAST pasted digit, since the old
  // handleOtpBoxChange did rawValue.slice(-1)) -- each box also has
  // maxLength={1}, so the browser truncates a paste down to one character
  // before onChange even fires, meaning fixing handleOtpBoxChange alone
  // wouldn't have been enough. Reading the clipboard directly here (before
  // the browser applies its own truncated paste, which preventDefault
  // stops from happening at all) lets a single paste distribute across
  // every box starting from wherever it was pasted, same as any other
  // 6-box OTP input (bank apps, 2FA prompts, etc.) -- this is what
  // "previously worked" back when this page was a single plain input
  // instead of 6 separate boxes.
  const handleOtpBoxPaste = (index, e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = pasted.replace(/[^0-9]/g, "");
    if (!digits) return;
    e.preventDefault();
    const boxes = otp.padEnd(6, " ").split("");
    let i = index;
    for (const d of digits) {
      if (i > 5) break;
      boxes[i] = d;
      i++;
    }
    setOtp(boxes.join("").trimEnd());
    otpBoxRefs.current[Math.min(i, 5)]?.focus();
  };

  const handleOtpBoxKeyDown = (index, e) => {
    if (e.key === "Enter") {
      verifyOtp(e);
      return;
    }
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      otpBoxRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="auth-box p-0 w-100">
      <div className="row w-100 g-0">
        <div className="col-md-7 d-none d-md-block">
          <div
            className="h-100 position-relative card-side-img rounded-0 overflow-hidden"
            style={{ backgroundImage: "url(/assets/images/logo/CET%20Background-withoutlogo.png)", backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="p-4 card-img-overlay auth-overlay d-flex align-items-end justify-content-center" />
          </div>
        </div>

        <div className="col-md-5">
          <div className="card auth-box-form border-0 mb-0">
            <div className="card-body min-vh-100 position-relative d-flex flex-column justify-content-center">
              <div className="auth-brand mb-3 text-center">
                <img src="/assets/images/logo/CET-Full-logo.png" alt="Cost Estimation Tool" style={{ maxWidth: 220 }} />
              </div>

              {totpRequired ? (
                <div>
                  <div className="text-center">
                    <h4 className="fw-bold text-dark">Enter your authenticator code</h4>
                    <p className="text-muted mb-0">Open your authenticator app for {email}</p>
                  </div>

                  {msg && (
                    <div className="alert alert-danger py-2 mt-3 mb-0" role="alert" aria-live="polite">
                      {msg}
                    </div>
                  )}

                  <form className="mt-4" onSubmit={verifyTotp}>
                    <div className="mb-3">
                      <label className="form-label d-flex">
                        6-digit authenticator code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className="form-control text-center"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, ""))}
                        autoFocus
                        required
                      />
                    </div>

                    <div className="d-grid">
                      <button
                        type="submit"
                        className="btn btn-primary fw-semibold py-2"
                        disabled={loading}
                      >
                        {loading ? "Verifying..." : "Verify"}
                      </button>
                    </div>
                  </form>

                  <p className="text-muted text-center mt-4 mb-0">
                    Return to{" "}
                    <a href="/" className="link-offset-3 fw-bold">Sign in</a>
                  </p>
                </div>
              ) : (
                <div>
                  <div className="text-center">
                    <h4 className="fw-bold text-dark">OTP sent on {email}</h4>
                  </div>

                  {msg && (
                    <div className="alert alert-danger py-2 mt-3 mb-0" role="alert" aria-live="polite">
                      {msg}
                    </div>
                  )}

                  <form className="mt-4" onSubmit={verifyOtp}>
                    <div className="mb-3">
                      <label className="form-label d-flex">
                        Enter your 6-digit code <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-2 two-factor">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <input
                            key={i}
                            ref={(el) => (otpBoxRefs.current[i] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className="form-control text-center"
                            value={otp[i] && otp[i] !== " " ? otp[i] : ""}
                            onChange={(e) => handleOtpBoxChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpBoxKeyDown(i, e)}
                            onPaste={(e) => handleOtpBoxPaste(i, e)}
                            required
                          />
                        ))}
                      </div>
                    </div>

                    <div className="d-grid">
                      <button
                        type="submit"
                        className="btn btn-primary fw-semibold py-2"
                        disabled={loading}
                      >
                        {loading ? "Verifying..." : "Verify OTP"}
                      </button>
                    </div>
                  </form>

                  <p className="text-muted text-center mt-4 mb-0">
                    Don&rsquo;t have a code?{" "}
                    {resendDisabled ? (
                      <span>Resend ({resendTimer}s)</span>
                    ) : (
                      <a href="#" className="link-offset-2 fw-bold" onClick={resendOtp}>
                        Resend
                      </a>
                    )}
                  </p>

                  <p className="text-muted text-center mt-4 mb-0">
                    Return to{" "}
                    <a href="/" className="link-offset-3 fw-bold">Sign in</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
